import React, { useState } from 'react';
import { Table, Badge, Card, Nav, Tab, Row, Col, Button } from 'react-bootstrap';
import { FaHome } from 'react-icons/fa';
import { DealDetail } from '../../services/dealService';
import dealService from '../../services/dealService';
import PropertyDetailsModal from '../../pages/customer-portal/PropertyDetailsModal';
import { Property } from '../../types/property';
import axiosInstance from '../../helpers/api';

interface DealCompsProps {
  deal: DealDetail;
  compDetails: any[];
}

const DealComps: React.FC<DealCompsProps> = ({ deal, compDetails }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [sortField, setSortField] = useState('comp_distance_miles');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

  // Parse top comps from deal - handle both string and array formats
  const parseComps = (comps: any): any[] => {
    if (!comps) return [];
    if (typeof comps === 'string') {
      try {
        return JSON.parse(comps);
      } catch (e) {
        console.error('Failed to parse comps:', e);
        return [];
      }
    }
    return Array.isArray(comps) ? comps : [];
  };

  const topRenovatedComps = parseComps(deal.top_renovated_comps);
  const topAsIsComps = parseComps(deal.top_as_is_comps);

  // Combine all comps with type flag
  const allComps = [
    ...topRenovatedComps.map(c => ({ ...c, comp_type: 'renovated' })),
    ...topAsIsComps.map(c => ({ ...c, comp_type: 'as-is' }))
  ];

  // Filter comps based on tab
  const getFilteredComps = () => {
    if (activeTab === 'renovated') {
      return topRenovatedComps.map(c => ({ ...c, comp_type: 'renovated' }));
    } else if (activeTab === 'as-is') {
      return topAsIsComps.map(c => ({ ...c, comp_type: 'as-is' }));
    }
    return allComps;
  };

  // Sort comps
  const sortedComps = [...getFilteredComps()].sort((a, b) => {
    const aValue = a[sortField] || 0;
    const bValue = b[sortField] || 0;
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleViewMLSDetails = async (comp: any) => {
    setIsLoadingPhotos(true);
    
    console.log('🏠 Comp data:', comp);
    console.log('📊 Comp fields:', {
      bedrooms_total: comp.bedrooms_total,
      bedrooms: comp.bedrooms,
      bathrooms_total: comp.bathrooms_total,
      bathrooms: comp.bathrooms,
      dom: comp.dom,
      days_on_market: comp.days_on_market
    });
    
    // Transform comp data into Property format for the modal
    const property: Property = {
      mlsNumber: comp.listing_id,
      address: comp.address,
      price: comp.close_price,
      squareFeet: comp.living_area,
      propertyType: comp.comp_type === 'renovated' ? 'Recently Renovated' : 'As-Is Sale',
      status: 'Closed',
      images: [], // Will be populated after fetching
      description: `Comparable ${comp.comp_type} sale located ${comp.distance_miles?.toFixed(1)} miles from subject property. Closed on ${new Date(comp.close_date).toLocaleDateString()} for $${comp.close_price?.toLocaleString()} at $${Math.round(comp.price_per_sqft || comp.comp_price_per_sqft || 0)}/sqft.`,
      // Use the correct field names from comp data
      bedrooms: comp.bedrooms_total || comp.bedrooms || 0,
      bathrooms: comp.bathrooms_total || comp.bathrooms || 0,
      yearBuilt: comp.year_built || 0,
      daysOnMarket: comp.dom || comp.days_on_market || 0,
    };
    
    // Show modal immediately with loading state
    setSelectedProperty(property);
    setShowPropertyModal(true);
    
    // Try to fetch MLS photos for this comp
    if (comp.listing_id) {
      console.log('🔍 Fetching MLS photos for comp listing:', comp.listing_id);
      try {
        // Use the exact MLS endpoint to ensure we get the right property
        const response = await axiosInstance.get(`/deals/mls/exact/${comp.listing_id}`);
        
        console.log('📸 MLS response for comp:', response.data);
        
        if (response.data.success && response.data.data) {
          const mlsData = response.data.data;
          const images = mlsData.images || [];
          const latitude = mlsData.latitude;
          const longitude = mlsData.longitude;
          
          console.log('📥 MLS Data received:', mlsData);
          console.log('🏘️ Property data:', mlsData.property_data);
          
          console.log(`✅ Found ${images.length} images for comp ${comp.listing_id}`);
          if (latitude && longitude) {
            console.log(`📍 Found coordinates for comp: ${latitude}, ${longitude}`);
            
            // Update comp object with coordinates for map
            comp.latitude = latitude;
            comp.longitude = longitude;
          }
          
          // Update the property with all MLS data
          setSelectedProperty({
            ...property,
            images: images,
            // Override with actual MLS data if available
            address: mlsData.address || property.address,
            description: mlsData.public_remarks || mlsData.property_data?.description || property.description,
            listingAgent: mlsData.listing_agent || undefined,
            virtualTourUrl: mlsData.virtual_tour_url || undefined,
            taxAmount: mlsData.tax_amount || undefined,
            hoaFee: mlsData.hoa_fee || undefined,
            // Update bedrooms/bathrooms/DOM from MLS data if available
            bedrooms: mlsData.property_data?.bedrooms || mlsData.bedrooms || property.bedrooms,
            bathrooms: mlsData.property_data?.bathrooms || mlsData.bathrooms || property.bathrooms,
            squareFeet: mlsData.property_data?.sqft || mlsData.property_data?.squareFeet || property.squareFeet,
            propertyType: mlsData.property_data?.propertyType || property.propertyType,
            daysOnMarket: mlsData.days_on_market || mlsData.property_data?.daysOnMarket || property.daysOnMarket,
            yearBuilt: mlsData.property_data?.yearBuilt || mlsData.yearBuilt || property.yearBuilt,
            schools: {
              elementary: mlsData.elementary_school || undefined,
              middle: mlsData.middle_school || undefined,
              high: mlsData.high_school || undefined
            },
            // Include additional MLS fields
            subdivision: mlsData.subdivision_name || undefined,
            status: mlsData.standard_status || property.status,
            latitude: latitude,
            longitude: longitude,
            listingOffice: mlsData.listing_office_name || undefined,
            listingBrokerage: mlsData.listing_office_name || mlsData.listing_agent?.brokerage || undefined
          });
        }
      } catch (error) {
        console.error('Failed to fetch MLS photos for comp:', error.response?.data || error);
        // Continue without photos if fetch fails
      }
    } else {
      console.log('⚠️  Comp has no listing_id:', comp);
    }
    
    setIsLoadingPhotos(false);
  };

  const getClassificationBadge = (compType: string) => {
    const variants: Record<string, string> = {
      'renovated': 'success',
      'as-is': 'primary',
      'as_is': 'primary',
      'neutral': 'secondary'
    };
    
    const label = compType === 'as-is' ? 'As-Is' : 
                  compType === 'renovated' ? 'Renovated' : 
                  compType;
    
    return (
      <Badge bg={variants[compType] || 'secondary'}>
        {label}
      </Badge>
    );
  };

  const calculateMedianPrice = (comps: any[]) => {
    if (comps.length === 0) return 0;
    const prices = comps.map(c => c.close_price || c.comp_close_price || 0).sort((a, b) => a - b);
    const mid = Math.floor(prices.length / 2);
    return prices.length % 2 !== 0 ? prices[mid] : (prices[mid - 1] + prices[mid]) / 2;
  };

  return (
    <div>
      {/* Summary Stats */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="mb-0">{deal.total_comps}</h4>
              <small className="text-muted">Total Comps Found</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="mb-0 text-success">{topRenovatedComps.length}</h4>
              <small className="text-muted">Renovated Comps</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="mb-0 text-primary">{topAsIsComps.length}</h4>
              <small className="text-muted">As-Is Comps</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="mb-0">{dealService.formatCurrency(deal.renovated_arv)}</h4>
              <small className="text-muted">Calculated ARV</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Comp Analysis Tabs */}
      <Card>
        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'all')}>
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="all">
                  All Comps
                  <Badge bg="secondary" className="ms-2">{compDetails.length}</Badge>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="renovated">
                  Renovated
                  <Badge bg="success" className="ms-2">
                    {topRenovatedComps.length}
                  </Badge>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="as-is">
                  As-Is
                  <Badge bg="primary" className="ms-2">
                    {topAsIsComps.length}
                  </Badge>
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey={activeTab}>
                {sortedComps.length > 0 ? (
                  <div className="table-responsive">
                    <Table hover>
                      <thead>
                        <tr>
                          <th>Address</th>
                          <th 
                            onClick={() => handleSort('close_price')}
                            style={{ cursor: 'pointer' }}
                          >
                            Sold Price
                          </th>
                          <th 
                            onClick={() => handleSort('close_date')}
                            style={{ cursor: 'pointer' }}
                          >
                            Close Date
                          </th>
                          <th>Sq Ft</th>
                          <th 
                            onClick={() => handleSort('price_per_sqft')}
                            style={{ cursor: 'pointer' }}
                          >
                            $/SqFt
                          </th>
                          <th 
                            onClick={() => handleSort('distance_miles')}
                            style={{ cursor: 'pointer' }}
                          >
                            Distance
                          </th>
                          <th>Type</th>
                          <th>MLS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedComps.map((comp, index) => (
                          <tr 
                            key={index}
                            style={comp.listing_id ? { cursor: 'pointer' } : {}}
                            onClick={comp.listing_id ? () => handleViewMLSDetails(comp) : undefined}
                            className={comp.listing_id ? 'clickable-row' : ''}
                          >
                            <td>
                              <div className="fw-medium">{comp.address}</div>
                            </td>
                            <td className="text-nowrap fw-bold">
                              {dealService.formatCurrency(comp.close_price)}
                            </td>
                            <td className="text-nowrap">
                              {new Date(comp.close_date).toLocaleDateString()}
                            </td>
                            <td>{comp.living_area?.toLocaleString()}</td>
                            <td>${Math.round(comp.price_per_sqft)}</td>
                            <td>{comp.distance_miles?.toFixed(2) || '0.00'} mi</td>
                            <td>{getClassificationBadge(comp.comp_type)}</td>
                            <td>
                              {comp.listing_id && (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewMLSDetails(comp);
                                  }}
                                  title="View Comp Details"
                                >
                                  <FaHome className="me-1" />
                                  View
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">No comps found for this filter</p>
                  </div>
                )}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>

          {/* Comp Statistics */}
          {sortedComps.length > 0 && (
            <Card className="mt-4 bg-light">
              <Card.Body>
                <h6>Statistics for {activeTab === 'all' ? 'All' : activeTab} Comps</h6>
                <Row>
                  <Col xs={6} md={3}>
                    <small className="text-muted">Median Sale Price</small>
                    <p className="mb-0 fw-bold">
                      {dealService.formatCurrency(calculateMedianPrice(sortedComps))}
                    </p>
                  </Col>
                  <Col xs={6} md={3}>
                    <small className="text-muted">Avg $/SqFt</small>
                    <p className="mb-0 fw-bold">
                      ${Math.round(
                        sortedComps.reduce((sum, c) => sum + (c.price_per_sqft || 0), 0) / 
                        sortedComps.length
                      )}
                    </p>
                  </Col>
                  <Col xs={6} md={3}>
                    <small className="text-muted">Avg Distance</small>
                    <p className="mb-0 fw-bold">
                      {(sortedComps.reduce((sum, c) => sum + (c.distance_miles || 0), 0) / 
                        sortedComps.length).toFixed(2)} mi
                    </p>
                  </Col>
                  <Col xs={6} md={3}>
                    <small className="text-muted">Date Range</small>
                    <p className="mb-0 fw-bold">Last 12 months</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}
        </Card.Body>
      </Card>

      {/* Property Details Modal */}
      <PropertyDetailsModal
        show={showPropertyModal}
        property={selectedProperty}
        onHide={() => {
          setShowPropertyModal(false);
          setSelectedProperty(null);
        }}
        onSave={(property) => {
          console.log('Property saved from comp view:', property);
        }}
      />
    </div>
  );
};

export default DealComps;